import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PublicBillSplitFacade } from '../../services/public-bill-split-facade';
import { SweetAlertService } from '@core/services/sweet-alert.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-public-bill-split-join-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './public-bill-split-join-page.html',
})
export class PublicBillSplitJoinPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private facade = inject(PublicBillSplitFacade);
  private sweetAlert = inject(SweetAlertService);

  private readonly PARTICIPANT_SESSION_MAX_AGE_MS = 2 * 60 * 60 * 1000;

  readonly splitId = this.route.snapshot.paramMap.get('splitId') ?? '';

  readonly splitTitle = signal('...');
  readonly splitStatus = signal<string | null>(null);
  readonly loadingPreview = signal(true);

  readonly name = signal('');
  readonly saving = signal(false);

  readonly isFormValid = computed(() => {
    const normalized = this.name().trim();
    return normalized.length >= 2;
  });

  readonly canJoin = computed(() => {
    const status = this.splitStatus();
    return status === 'DRAFT' || status === 'COLLECTING';
  });

  async ngOnInit(): Promise<void> {
    if (!this.splitId) {
      this.loadingPreview.set(false);
      this.sweetAlert.showError(
        'Enlace inválido',
        'No encontramos la división a la que querés unirte.',
      );
      return;
    }

    const storedSession = this.getStoredParticipantSession();

    if (storedSession && storedSession.splitId === this.splitId) {
      if (this.isStoredSessionExpired(storedSession.savedAt)) {
        this.clearParticipantSession(this.splitId);
      } else {
        const result = await this.sweetAlert.showChoice(
          'Ya tenías una sesión activa',
          `Encontramos una sesión como ${storedSession.participantName}. ¿Querés continuar con esa persona o unirte como participante nuevo?`,
          `Continuar como ${storedSession.participantName}`,
          'Unirme como nuevo',
        );

        if (result.isConfirmed) {
          this.router.navigate(['/division-cuenta/participar', this.splitId], {
            replaceUrl: true,
          });
          return;
        }

        const choseNewParticipant =
          result.isDismissed && result.dismiss === Swal.DismissReason.cancel;

        if (choseNewParticipant) {
          this.clearParticipantSession(this.splitId);
        } else {
          return;
        }
      }
    }

    this.facade.getJoinPreview(this.splitId).subscribe({
      next: (response) => {
        this.splitTitle.set(response.title);
        this.splitStatus.set(response.status);
        this.loadingPreview.set(false);
      },
      error: (err: unknown) => {
        this.loadingPreview.set(false);
        this.sweetAlert.showError(
          'No se pudo abrir la invitación',
          'Revisá el enlace o intentá nuevamente.',
          err,
        );
      },
    });
  }

  goBack(): void {
    this.router.navigate(['/']);
  }

  join(): void {
    if (this.hasExistingJoinSessionForThisSplit()) {
      this.router.navigate(['/division-cuenta/participar', this.splitId], {
        replaceUrl: true,
      });
      return;
    }

    if (!this.splitId || !this.isFormValid() || this.saving() || !this.canJoin()) {
      return;
    }

    this.saving.set(true);

    this.facade.joinSplit(this.splitId, this.name().trim()).subscribe({
      next: (response) => {
        this.cleanOldParticipantSessionData(this.splitId);

        sessionStorage.setItem('dinno_join_split_id', this.splitId);
        sessionStorage.setItem('dinno_join_participant_id', response.id);
        sessionStorage.setItem('dinno_join_participant_name', response.participantName);
        sessionStorage.setItem('dinno_join_participant_token', response.participantToken);
        sessionStorage.setItem('dinno_join_saved_at', String(Date.now()));

        this.router.navigate(['/division-cuenta/participar', this.splitId]);
      },
      error: (err: unknown) => {
        this.saving.set(false);
        this.sweetAlert.showError(
          'No se pudo unir a la división',
          'Revisá el enlace o intentá nuevamente en unos segundos.',
          err,
        );
      },
    });
  }

  private hasExistingJoinSessionForThisSplit(): boolean {
    const storedSplitId = sessionStorage.getItem('dinno_join_split_id');
    const participantToken = sessionStorage.getItem('dinno_join_participant_token');
    const participantId = sessionStorage.getItem('dinno_join_participant_id');

    return storedSplitId === this.splitId && !!participantToken && !!participantId;
  }

  private getStoredParticipantSession() {
    const splitId = sessionStorage.getItem('dinno_join_split_id');
    const participantId = sessionStorage.getItem('dinno_join_participant_id');
    const participantName = sessionStorage.getItem('dinno_join_participant_name');
    const participantToken = sessionStorage.getItem('dinno_join_participant_token');
    const savedAtRaw = sessionStorage.getItem('dinno_join_saved_at');

    if (!splitId || !participantId || !participantName || !participantToken || !savedAtRaw) {
      return null;
    }

    const savedAt = Number(savedAtRaw);
    if (!Number.isFinite(savedAt)) {
      return null;
    }

    return {
      splitId,
      participantId,
      participantName,
      participantToken,
      savedAt,
    };
  }

  private isStoredSessionExpired(savedAt: number): boolean {
    return Date.now() - savedAt > this.PARTICIPANT_SESSION_MAX_AGE_MS;
  }

  private clearParticipantSession(splitId?: string): void {
    const currentSplitId = splitId ?? sessionStorage.getItem('dinno_join_split_id');

    sessionStorage.removeItem('dinno_join_split_id');
    sessionStorage.removeItem('dinno_join_participant_id');
    sessionStorage.removeItem('dinno_join_participant_name');
    sessionStorage.removeItem('dinno_join_participant_token');
    sessionStorage.removeItem('dinno_join_saved_at');

    if (currentSplitId) {
      sessionStorage.removeItem(`dinno_participant_submitted_${currentSplitId}`);
    }
  }

  private cleanOldParticipantSessionData(currentSplitId: string): void {
    const prefixesToClean = ['dinno-sse-client-participant-', 'dinno_participant_submitted_'];

    Object.keys(sessionStorage).forEach((key) => {
      const shouldClean = prefixesToClean.some((prefix) => key.startsWith(prefix));
      const belongsToCurrentSplit = key.endsWith(currentSplitId);

      if (shouldClean && !belongsToCurrentSplit) {
        sessionStorage.removeItem(key);
      }
    });
  }
}
